import React, { useState, useMemo } from 'react';
import { CustomerListItem } from '../../types';
import { useNavigate } from 'react-router-dom';

interface CustomerTableProps {
    customers: CustomerListItem[];
    showOwnerColumn?: boolean;
    enableRowClick?: boolean;
    fromDashboard?: boolean | ((customerId: string) => boolean);
}

type SortField = 'name' | 'industry_type' | 'created_by' | 'last_updated';
type SortDirection = 'asc' | 'desc';

const CustomerTable: React.FC<CustomerTableProps> = ({ 
    customers, 
    showOwnerColumn = false,
    enableRowClick = true,
    fromDashboard = false
}) => {
    const navigate = useNavigate();
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const handleCustomerClick = (customerId: string) => {
        if (enableRowClick) {
            // Determine if we should use fromDashboard as a boolean or call it as a function
            const isDashboard = typeof fromDashboard === 'function' 
                ? fromDashboard(customerId) 
                : fromDashboard;
                
            navigate(`/customer-details/${customerId}`, {
                state: { fromDashboard: isDashboard }
            });
        }
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            // Toggle direction if clicking the same field
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new field and default to ascending
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortedCustomers = useMemo(() => {
        return [...customers].sort((a, b) => {
            // Handle undefined values
            const aValue = a[sortField] || '';
            const bValue = b[sortField] || '';
            
            // Compare based on direction
            if (sortDirection === 'asc') {
                return aValue.toString().localeCompare(bValue.toString());
            } else {
                return bValue.toString().localeCompare(aValue.toString());
            }
        });
    }, [customers, sortField, sortDirection]);

    // Helper to render sort indicator
    const renderSortIndicator = (field: SortField) => {
        if (sortField !== field) {
            return (
                <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12L6 17L11 12M1 6L6 1L11 6" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        }
        
        return sortDirection === 'asc' ? (
            <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 6L6 1L11 6" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ) : (
            <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 12L6 17L11 12" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
    };

    return (
        <div className="overflow-x-auto shadow">
            <table className="w-full border-collapse bg-transparent bg-gray-50 border">
                <thead className="bg-[#f0f6f7]">
                    <tr>
                        <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F] border-r">
                            <div 
                                className="flex justify-between items-center cursor-pointer" 
                                onClick={() => handleSort('name')}
                            >
                                Customer Name
                                {renderSortIndicator('name')}
                            </div>
                        </th>
                        <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F] border-r">
                            <div 
                                className="flex justify-between items-center cursor-pointer"
                                onClick={() => handleSort('industry_type')}
                            >
                                Industry
                                {renderSortIndicator('industry_type')}
                            </div>
                        </th>
                        <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F]">
                            {showOwnerColumn ? (
                                <div 
                                    className="flex justify-between items-center cursor-pointer"
                                    onClick={() => handleSort('created_by')}
                                >
                                    Customer Owner
                                    {renderSortIndicator('created_by')}
                                </div>
                            ) : (
                                <div 
                                    className="flex justify-between items-center cursor-pointer"
                                    onClick={() => handleSort('last_updated')}
                                >
                                    Activity
                                    {renderSortIndicator('last_updated')}
                                </div>
                            )}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedCustomers.map((customer, index) => (
                        <tr
                            key={customer.customer_id}
                            className={`border-b border-gray-200 bg-transparent ${enableRowClick ? 'hover:bg-gray-100 cursor-pointer' : ''}`}
                            onClick={() => handleCustomerClick(customer.customer_id)}
                        >
                            <td className="p-3 border-r">
                                <div className="text-md font-semibold text-[#0F2A4F] italic">{customer.name}</div>
                            </td>
                            <td className="p-3 border-r">
                                <div className="text-md font-normal text-[#0F2A4F] italic">{customer.industry_type || ''}</div>
                            </td>
                            <td className="p-3">
                                {showOwnerColumn ? (
                                    <div className="text-sm text-[#0F2A4F]">{customer.created_by}</div>
                                ) : (
                                    <div className="text-sm text-[#0F2A4F] italic">
                                        {customer.last_updated ? (
                                            <>
                                                Last updated on{' '}
                                                <span className="font-medium">
                                                    {new Date(customer.last_updated).toLocaleDateString('en-US', {
                                                        weekday: 'short',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        second: '2-digit',
                                                        hour12: false,
                                                    })}
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                Created on{' '}
                                                <span className="font-medium">
                                                    {new Date().toLocaleDateString('en-US', {
                                                        weekday: 'short',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        second: '2-digit',
                                                        hour12: false,
                                                    })}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                    {sortedCustomers.length === 0 && (
                        <tr>
                            <td colSpan={3} className="px-6 py-10 text-center text-gray-500">
                                No customers found
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default CustomerTable;
